import { Link } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePreserveQueryParams, useDemoMode } from '../hooks/usePreserveQueryParams'
import { DEMO_BRAND_NAME } from '../utils/demoData'
import {
  LayoutDashboard,
  TrendingUp,
  Bot,
  Search,
  Users,
  ArrowRight,
  RefreshCw
} from 'lucide-react'

interface DashboardHeaderProps {
  stats: {
    aiIndexScore?: number
    aiMentionRate?: number
    trackedQueries?: number
    competitorCount?: number
  } | null
  loading?: boolean
  onRefresh?: () => void
  refreshing?: boolean
}

export default function DashboardHeader({
  stats,
  loading = false,
  onRefresh,
  refreshing = false
}: DashboardHeaderProps) {
  const { activeBrand } = useAuth()
  const { withParams } = usePreserveQueryParams()
  const { isDemoMode } = useDemoMode()

  if (loading) {
    return (
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg animate-pulse">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <div className="h-4 bg-white/20 rounded w-32 mb-3"></div>
            <div className="h-8 bg-white/20 rounded w-64 mb-2"></div>
            <div className="h-4 bg-white/20 rounded w-96"></div>
          </div>
          <div className="flex flex-wrap gap-3">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white/20 rounded-lg px-4 py-3 min-w-[100px] h-16"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white shadow-lg">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        {/* Left: Title & Description */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <LayoutDashboard className="w-5 h-5 text-indigo-200" />
            <span className="text-indigo-200 text-sm font-medium uppercase tracking-wide">Brand Overview</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold mb-2">
            {isDemoMode ? DEMO_BRAND_NAME : (activeBrand?.companyName || 'Your Brand')}<span className="text-indigo-200"> Dashboard</span>
          </h2>
          <p className="text-indigo-100 text-sm md:text-base">
            Track your AI visibility score, monitor mentions, and outperform competitors
          </p>
        </div>

        {/* Right: Stats Badges - Grid on mobile, flex on desktop */}
        <div className="grid grid-cols-2 md:flex md:flex-wrap gap-2 md:gap-3 w-full md:w-auto">
          {/* AI Index Score */}
          <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <TrendingUp className="w-4 h-4" />
              <span className="text-xl md:text-2xl font-bold">
                {stats?.aiIndexScore != null ? stats.aiIndexScore : '--'}
              </span>
            </div>
            <p className="text-xs text-indigo-200">AI Index</p>
          </div>

          {/* AI Mention Rate */}
          <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Bot className="w-4 h-4" />
              <span className="text-xl md:text-2xl font-bold">
                {stats?.aiMentionRate != null
                  ? `${Math.round(stats.aiMentionRate)}%`
                  : '--'}
              </span>
            </div>
            <p className="text-xs text-indigo-200">Mention Rate</p>
          </div>

          {/* Tracked Queries */}
          <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Search className="w-4 h-4" />
              <span className="text-xl md:text-2xl font-bold">
                {stats?.trackedQueries ?? '--'}
              </span>
            </div>
            <p className="text-xs text-indigo-200">Queries</p>
          </div>

          {/* Competitors */}
          <div className="bg-white/20 rounded-lg px-3 md:px-4 py-2 md:py-3 text-center flex-1 md:flex-none md:min-w-[100px]">
            <div className="flex items-center justify-center gap-1.5 mb-1">
              <Users className="w-4 h-4" />
              <span className="text-xl md:text-2xl font-bold">
                {stats?.competitorCount ?? '--'}
              </span>
            </div>
            <p className="text-xs text-indigo-200">Competitors</p>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap gap-3">
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={refreshing}
            className="inline-flex items-center px-4 py-2 bg-white/20 text-white rounded-lg hover:bg-white/30 font-medium transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh Data'}
          </button>
        )}
        <Link
          to={withParams('/search')}
          className="inline-flex items-center px-4 py-2 bg-white text-indigo-600 rounded-lg hover:bg-indigo-50 font-medium transition-colors"
        >
          <Search className="w-4 h-4 mr-2" />
          Search Intelligence
          <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
        <Link
          to={withParams('/competition')}
          className="inline-flex items-center px-4 py-2 bg-white/20 text-white border border-white/30 rounded-lg hover:bg-white/30 font-medium transition-colors"
        >
          <Users className="w-4 h-4 mr-2" />
          View Competitors
        </Link>
      </div>
    </div>
  )
}
